import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { translations, languageNames, languageFlags } from '../lib/translations';
import { ArrowLeft, CalendarBlank, CurrencyEur, House, Warning, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const languages = ['ITA', 'ENG', 'FRA', 'SPA', 'TED'];

export default function NewContractPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);

  const lang = user?.preferred_language || localStorage.getItem('selectedLanguage') || 'ITA';
  const t = translations[lang];

  const [formData, setFormData] = useState({
    language: lang,
    property_id: '',
    template_id: '',
    nights: 1,
    check_in_date: '',
    check_in_time: '14:00',
    check_out_date: '',
    check_out_time: '10:00',
    total_rent: '',
    deposit_amount: 0,
    balance_amount: 0,
    tourist_tax: 0,
    payment_method: '',
    security_deposit_required: false,
    security_deposit_amount: 0,
    place_date: ''
  });

  // Proprietario diverso dall'utente (override ad-hoc per questo contratto)
  const [ownerDifferent, setOwnerDifferent] = useState(false);
  const [ownerOverride, setOwnerOverride] = useState({
    full_name: '',
    birth_place: '',
    birth_date: '',
    residence: '',
    tax_code: '',
  });

  const [availableTemplates, setAvailableTemplates] = useState([]);

  useEffect(() => {
    fetchProperties();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const r = await axios.get(`${API}/templates`, { withCredentials: true });
      setAvailableTemplates(r.data || []);
    } catch (err) {
      // Non fatal — proceeds with default contract
      console.error('Error fetching templates:', err);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data);
      // Auto-select if only one property
      if (response.data.length === 1) {
        setFormData(prev => ({ ...prev, property_id: response.data[0].id }));
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-calculate balance
      if (field === 'total_rent' || field === 'deposit_amount') {
        const total = parseFloat(updated.total_rent) || 0;
        const deposit = parseFloat(updated.deposit_amount) || 0;
        updated.balance_amount = Math.max(0, total - deposit);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id) {
      toast.error('Seleziona una struttura');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        total_rent: parseFloat(formData.total_rent) || 0,
        deposit_amount: parseFloat(formData.deposit_amount) || 0,
        balance_amount: parseFloat(formData.balance_amount) || 0,
        tourist_tax: parseFloat(formData.tourist_tax) || 0,
        security_deposit_amount: parseFloat(formData.security_deposit_amount) || 0,
        nights: parseInt(formData.nights)
      };
      // Convert empty string to null so backend Optional[str] accepts it
      if (!payload.template_id) payload.template_id = null;
      if (ownerDifferent && ownerOverride.full_name.trim()) {
        payload.owner_override = ownerOverride;
      }
      const response = await axios.post(`${API}/contracts`, payload, { withCredentials: true });
      
      toast.success(t.contractCreated);
      navigate(`/contract/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione del contratto');
    } finally {
      setLoading(false);
    }
  };

  // Show message if no properties
  if (!loadingProperties && properties.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <header className="bg-white border-b border-zinc-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-zinc-100 transition-colors"
            >
              <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
            </button>
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">{t.newContract}</h1>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="bg-white border border-zinc-200 p-8 text-center">
            <Warning size={48} className="text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Nessuna struttura registrata</h2>
            <p className="text-zinc-500 mb-6">
              Prima di creare un contratto devi registrare almeno una struttura.
            </p>
            <Button
              onClick={() => navigate('/properties')}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            >
              <House size={20} className="mr-2" />
              Aggiungi Alloggio
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-zinc-100 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">{t.newContract}</h1>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Property Selection */}
          {properties.length > 1 && (
            <div className="bg-white border border-zinc-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <House size={24} weight="bold" className="text-zinc-700" />
                <h2 className="text-lg font-bold text-zinc-900">Seleziona Struttura</h2>
              </div>
              <Select 
                value={formData.property_id} 
                onValueChange={(v) => handleChange('property_id', v)}
              >
                <SelectTrigger className="rounded-none" data-testid="property-select">
                  <SelectValue placeholder="Scegli la struttura..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-2">
                        <House size={16} />
                        <span>{p.name}</span>
                        <span className="text-zinc-400 text-xs">- {p.property_city}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* If only one property, show it as info */}
          {properties.length === 1 && (
            <div className="bg-zinc-100 border border-zinc-200 p-4 flex items-center gap-3">
              <House size={20} className="text-zinc-600" />
              <div>
                <span className="font-medium text-zinc-900">{properties[0].name}</span>
                <span className="text-zinc-500 text-sm ml-2">- {properties[0].property_city}</span>
              </div>
            </div>
          )}

          {/* Template Selection — only if the host has saved templates */}
          {availableTemplates.length > 0 && (
            <div className="bg-white border border-zinc-200 p-6" data-testid="template-selector-block">
              <div className="flex items-center gap-3 mb-2">
                <FileText size={24} weight="bold" className="text-teal-700" />
                <h2 className="text-lg font-bold text-zinc-900">Modello contratto</h2>
              </div>
              <p className="text-xs text-zinc-500 mb-3">
                Scegli quale modello utilizzare per questo contratto. Le clausole personalizzate del modello selezionato verranno aggiunte al contratto standard.
              </p>
              <Select
                value={formData.template_id || 'default'}
                onValueChange={(v) => handleChange('template_id', v === 'default' ? '' : v)}
              >
                <SelectTrigger className="rounded-none" data-testid="template-select">
                  <SelectValue placeholder="Contratto standard di sistema" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default" data-testid="template-option-default">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-zinc-400"></span>
                      <span className="font-medium">Contratto standard di sistema</span>
                    </span>
                  </SelectItem>
                  {availableTemplates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id} data-testid={`template-option-${tpl.id}`}>
                      <span className="flex items-center gap-2">
                        <FileText size={14} className="text-teal-700" />
                        <span>{tpl.name}</span>
                        <span className="text-zinc-400 text-xs">· {tpl.language}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.template_id && (
                <p className="text-[11px] text-teal-700 mt-2 flex items-center gap-1.5">
                  ✓ Verrà utilizzato il modello personalizzato selezionato per generare le clausole del contratto
                </p>
              )}
            </div>
          )}

          {/* Language Selection */}
          <div className="bg-white border border-zinc-200 p-6">
            <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-3 block">
              {t.selectLanguage}
            </Label>
            <Select value={formData.language} onValueChange={(v) => handleChange('language', v)}>
              <SelectTrigger className="rounded-none" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map(l => (
                  <SelectItem key={l} value={l}>
                    <span className="flex items-center gap-2">
                      <span>{languageFlags[l]}</span>
                      <span>{languageNames[l]}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stay Duration */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <CalendarBlank size={24} weight="bold" className="text-zinc-700" />
              <h2 className="text-lg font-bold text-zinc-900">{t.stayDuration}</h2>
            </div>

            <div className="grid gap-5">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.nights}</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.nights}
                  onChange={(e) => handleChange('nights', e.target.value)}
                  required
                  className="rounded-none mt-2"
                  data-testid="nights-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.checkIn} - Data</Label>
                  <Input
                    type="date"
                    value={formData.check_in_date}
                    onChange={(e) => handleChange('check_in_date', e.target.value)}
                    required
                    className="rounded-none mt-2"
                    data-testid="checkin-date-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.checkIn} - Ora</Label>
                  <Input
                    type="time"
                    value={formData.check_in_time}
                    onChange={(e) => handleChange('check_in_time', e.target.value)}
                    required
                    className="rounded-none mt-2"
                    data-testid="checkin-time-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.checkOut} - Data</Label>
                  <Input
                    type="date"
                    value={formData.check_out_date}
                    onChange={(e) => handleChange('check_out_date', e.target.value)}
                    required
                    className="rounded-none mt-2"
                    data-testid="checkout-date-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.checkOut} - Ora</Label>
                  <Input
                    type="time"
                    value={formData.check_out_time}
                    onChange={(e) => handleChange('check_out_time', e.target.value)}
                    required
                    className="rounded-none mt-2"
                    data-testid="checkout-time-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Owner override (proprietario diverso dall'utente account) */}
          <div className="bg-white border border-zinc-200 p-6" data-testid="owner-override-section">
            <div className="flex items-start sm:items-center justify-between gap-3 mb-4 flex-col sm:flex-row">
              <div className="flex items-start gap-3">
                <House size={22} weight="bold" className="text-zinc-700 flex-shrink-0 mt-1 sm:mt-0" />
                <div>
                  <h2 className="text-lg font-bold text-zinc-900 tracking-tight">Proprietario dell'immobile</h2>
                  <p className="text-xs text-zinc-500 mt-0.5">Compila se chi firma il contratto è diverso da te (es. gestisci per un altro).</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch
                  checked={ownerDifferent}
                  onCheckedChange={setOwnerDifferent}
                  data-testid="owner-different-toggle"
                />
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Proprietario diverso</Label>
              </div>
            </div>

            {ownerDifferent && (
              <div className="grid gap-4 pt-2 border-t border-zinc-100">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Nome e cognome *</Label>
                  <Input
                    type="text"
                    value={ownerOverride.full_name}
                    onChange={(e) => setOwnerOverride({ ...ownerOverride, full_name: e.target.value })}
                    placeholder="Mario Rossi"
                    className="rounded-none mt-2"
                    data-testid="owner-override-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Luogo di nascita</Label>
                    <Input
                      type="text"
                      value={ownerOverride.birth_place}
                      onChange={(e) => setOwnerOverride({ ...ownerOverride, birth_place: e.target.value })}
                      placeholder="Milano (MI)"
                      className="rounded-none mt-2"
                      data-testid="owner-override-birth-place"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Data di nascita</Label>
                    <Input
                      type="date"
                      value={ownerOverride.birth_date}
                      onChange={(e) => setOwnerOverride({ ...ownerOverride, birth_date: e.target.value })}
                      className="rounded-none mt-2"
                      data-testid="owner-override-birth-date"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Residenza completa</Label>
                  <Input
                    type="text"
                    value={ownerOverride.residence}
                    onChange={(e) => setOwnerOverride({ ...ownerOverride, residence: e.target.value })}
                    placeholder="Via Roma 10, 20100 Milano (MI)"
                    className="rounded-none mt-2"
                    data-testid="owner-override-residence"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Codice Fiscale</Label>
                  <Input
                    type="text"
                    value={ownerOverride.tax_code}
                    onChange={(e) => setOwnerOverride({ ...ownerOverride, tax_code: e.target.value.toUpperCase() })}
                    placeholder="RSSMRA80A01F205X"
                    maxLength={16}
                    className="rounded-none mt-2 font-mono"
                    data-testid="owner-override-tax-code"
                  />
                </div>
                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-xs text-amber-900 leading-relaxed">
                  <strong>Nota:</strong> i dati dell'immobile (indirizzo, catasto, CIN) restano quelli della tua proprietà. Solo l'anagrafica del Locatore viene sostituita in questo contratto.
                </div>
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <CurrencyEur size={24} weight="bold" className="text-zinc-700" />
              <h2 className="text-lg font-bold text-zinc-900">{t.rentPayment}</h2>
            </div>

            <div className="grid gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.totalRent} (€) <span className="text-zinc-400 font-normal normal-case">(opzionale)</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={formData.total_rent}
                    onChange={(e) => handleChange('total_rent', e.target.value)}
                    placeholder="0,00 — lascia vuoto se usi solo per tassa di soggiorno"
                    className="rounded-none mt-2"
                    data-testid="total-rent-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.deposit} (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={formData.deposit_amount}
                    onChange={(e) => handleChange('deposit_amount', e.target.value)}
                    className="rounded-none mt-2"
                    data-testid="deposit-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.balance} (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.balance_amount}
                    readOnly
                    className="rounded-none mt-2 bg-zinc-50"
                    data-testid="balance-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.touristTax} (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={formData.tourist_tax}
                    onChange={(e) => handleChange('tourist_tax', e.target.value)}
                    className="rounded-none mt-2"
                    data-testid="tourist-tax-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.paymentMethod}</Label>
                <Input
                  value={formData.payment_method}
                  onChange={(e) => handleChange('payment_method', e.target.value)}
                  placeholder="es. Bonifico, Contanti, Carta"
                  required
                  className="rounded-none mt-2"
                  data-testid="payment-method-input"
                />
              </div>

              <div className="border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-sm font-medium text-zinc-700">{t.securityDeposit}</Label>
                  <Switch
                    checked={formData.security_deposit_required}
                    onCheckedChange={(checked) => handleChange('security_deposit_required', checked)}
                    data-testid="security-deposit-switch"
                  />
                </div>
                {formData.security_deposit_required && (
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">Importo (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={formData.security_deposit_amount}
                      onChange={(e) => handleChange('security_deposit_amount', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="security-deposit-amount-input"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Place and Date */}
          <div className="bg-white border border-zinc-200 p-6">
            <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.placeDate}</Label>
            <Input
              value={formData.place_date}
              onChange={(e) => handleChange('place_date', e.target.value)}
              placeholder="es. Roma, 15/01/2026"
              required
              className="rounded-none mt-2"
              data-testid="place-date-input"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex-1 rounded-none border-zinc-900 text-zinc-900 font-bold uppercase tracking-wider py-6"
              data-testid="cancel-button"
            >
              {t.cancel}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
              data-testid="create-contract-button"
            >
              {loading ? '...' : t.save}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
